import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useUser, useEmploymentRole } from '@/features/auth';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { WIDGET_DEFS, DEFAULT_WIDGET_IDS, filterWidgetsByPermissions, WIDGET_MAP } from './widgetRegistry';
import { widgetApi } from './widgetApi';
import type { WidgetDef } from './widgetRegistry';
import type { LayoutItem, Layout, ResponsiveLayouts } from 'react-grid-layout';

const STORAGE_KEY = 'brainsway_widget_layout';
const CONFIG_VERSION = 9;

interface WidgetConfig {
  version: number;
  layouts: { lg: LayoutItem[]; md: LayoutItem[]; sm: LayoutItem[] };
  activeWidgetIds: string[];
  hiddenWidgetIds: string[];
}

function buildColumnLayout(ids: string[], cols: number): LayoutItem[] {
  const items: LayoutItem[] = [];
  let y = 0;
  let pendingHalf: { h: number } | null = null;

  ids.forEach((id) => {
    const def = WIDGET_MAP.get(id);
    if (!def) return;

    const { w, h } = def.defaultSize;

    if (cols === 1) {
      // Single column (mobile)
      items.push({ i: id, x: 0, y, w: 1, h, minW: 1, minH: def.minSize.h });
      y += h;
      return;
    }

    if (w >= 12) {
      // Full-width widget — flush any pending half first
      if (pendingHalf) {
        y += pendingHalf.h;
        pendingHalf = null;
      }
      items.push({ i: id, x: 0, y, w: 12, h, minW: def.minSize.w, minH: def.minSize.h });
      y += h;
    } else if (!pendingHalf) {
      // First half-width in a pair — place at x=0, don't advance y yet
      items.push({ i: id, x: 0, y, w: 6, h, minW: def.minSize.w, minH: def.minSize.h });
      pendingHalf = { h };
    } else {
      // Second half-width — place at x=6, advance y by tallest of pair
      items.push({ i: id, x: 6, y, w: 6, h, minW: def.minSize.w, minH: def.minSize.h });
      y += Math.max(pendingHalf.h, h);
      pendingHalf = null;
    }
  });

  // Flush trailing unpaired half
  if (pendingHalf) {
    y += pendingHalf.h;
  }

  return items;
}

function buildDefaultLayouts(ids: string[]): { lg: LayoutItem[]; md: LayoutItem[]; sm: LayoutItem[] } {
  const layout = buildColumnLayout(ids, 12);
  return {
    lg: layout,
    md: layout,
    sm: buildColumnLayout(ids, 1),
  };
}

function buildDefaults(permittedIds: string[]): WidgetConfig {
  const activeIds = DEFAULT_WIDGET_IDS.filter((id) => permittedIds.includes(id));
  const hiddenIds = permittedIds.filter((id) => !activeIds.includes(id));
  return {
    version: CONFIG_VERSION,
    layouts: buildDefaultLayouts(activeIds),
    activeWidgetIds: activeIds,
    hiddenWidgetIds: hiddenIds,
  };
}

function layoutsEqual(a: LayoutItem[], b: LayoutItem[]): boolean {
  if (a.length !== b.length) return false;
  for (let idx = 0; idx < a.length; idx++) {
    const ai = a[idx], bi = b[idx];
    if (ai.i !== bi.i || ai.x !== bi.x || ai.y !== bi.y || ai.w !== bi.w || ai.h !== bi.h) return false;
  }
  return true;
}

function patchMinSizes(items: LayoutItem[]): LayoutItem[] {
  return items.map((item) => {
    const def = WIDGET_MAP.get(item.i);
    if (!def) return item;
    return { ...item, minW: def.minSize.w, minH: def.minSize.h };
  });
}

function loadConfig(userId: string): WidgetConfig | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const allConfigs = JSON.parse(stored);
    const cfg = allConfigs[userId] ?? null;
    if (!cfg || cfg.version !== CONFIG_VERSION) return null;
    // Always re-apply current minSize from registry
    cfg.layouts.lg = patchMinSizes(cfg.layouts.lg);
    cfg.layouts.md = patchMinSizes(cfg.layouts.md);
    cfg.layouts.sm = patchMinSizes(cfg.layouts.sm);
    return cfg;
  } catch {
    return null;
  }
}

function saveConfig(userId: string, config: WidgetConfig): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const allConfigs = stored ? JSON.parse(stored) : {};
    allConfigs[userId] = config;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allConfigs));
  } catch {
    // ignore storage errors
  }
}

export function useWidgetStore() {
  const user = useUser();
  const { canCreateReports } = useEmploymentRole();
  const { hasPermission, isAdmin } = usePermissions();
  const userId = user?.id ?? 'anonymous';

  const permittedWidgets = useMemo(
    () => filterWidgetsByPermissions(WIDGET_DEFS, { canCreateReports, isAdmin: isAdmin(), hasPermission }),
    [canCreateReports, isAdmin, hasPermission],
  );
  const permittedIds = useMemo(() => permittedWidgets.map((w) => w.id), [permittedWidgets]);

  const [config, setConfig] = useState<WidgetConfig>(() => {
    const saved = loadConfig(userId);
    if (saved) {
      const validActive = saved.activeWidgetIds.filter((id) => permittedIds.includes(id));
      if (validActive.length !== saved.activeWidgetIds.length) {
        return { ...saved, activeWidgetIds: validActive };
      }
      return saved;
    }
    return buildDefaults(permittedIds);
  });

  // Keep a ref to always have the latest config (avoids stale closures)
  const configRef = useRef(config);
  configRef.current = config;

  // Re-load config when the real userId becomes available (Zustand persist hydrates async)
  const prevUserIdRef = useRef(userId);
  useEffect(() => {
    if (prevUserIdRef.current === userId) return;
    prevUserIdRef.current = userId;
    const saved = loadConfig(userId);
    if (saved) {
      const validActive = saved.activeWidgetIds.filter((id) => permittedIds.includes(id));
      const next = validActive.length !== saved.activeWidgetIds.length
        ? { ...saved, activeWidgetIds: validActive }
        : saved;
      configRef.current = next;
      setConfig(next);
    } else {
      const next = buildDefaults(permittedIds);
      configRef.current = next;
      setConfig(next);
    }
  }, [userId, permittedIds]);

  // Debounced API save — only fires 2s after last call
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveToApi = useCallback((cfg: WidgetConfig) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      widgetApi.saveConfig(cfg).catch((err: unknown) => {
        console.error('Failed to save widget config to API:', err);
      });
    }, 2000);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const persist = useCallback(
    (next: WidgetConfig) => {
      configRef.current = next;
      setConfig(next);
      saveConfig(userId, next);
      saveToApi(next);
    },
    [userId, saveToApi],
  );

  // Load from API on mount / userId change — API is source of truth
  const apiFetchedRef = useRef<string | null>(null);
  useEffect(() => {
    if (userId === 'anonymous' || apiFetchedRef.current === userId) return;
    apiFetchedRef.current = userId;

    widgetApi.getConfig().then(async (res) => {
      const apiConfig = res.data?.widgetConfig as WidgetConfig | null;
      if (apiConfig && apiConfig.version === CONFIG_VERSION) {
        // Patch minSizes from registry
        apiConfig.layouts.lg = patchMinSizes(apiConfig.layouts.lg);
        apiConfig.layouts.md = patchMinSizes(apiConfig.layouts.md);
        apiConfig.layouts.sm = patchMinSizes(apiConfig.layouts.sm);
        // Filter to permitted widgets
        const validActive = apiConfig.activeWidgetIds.filter((id) => permittedIds.includes(id));
        const patched = validActive.length !== apiConfig.activeWidgetIds.length
          ? { ...apiConfig, activeWidgetIds: validActive }
          : apiConfig;
        configRef.current = patched;
        setConfig(patched);
        saveConfig(userId, patched); // cache locally
      } else if (!apiConfig) {
        // No user config — try fetching a default template (role/employmentRole)
        try {
          const tplRes = await widgetApi.getMyTemplate();
          const tpl = tplRes.data?.template;
          if (tpl && tpl.config && Array.isArray(tpl.config.activeWidgetIds)) {
            const tplActiveIds = tpl.config.activeWidgetIds.filter((id: string) => permittedIds.includes(id));
            const tplHiddenIds = permittedIds.filter((id) => !tplActiveIds.includes(id));
            const tplLayouts = tpl.config.layouts ?? buildDefaultLayouts(tplActiveIds);
            const tplConfig: WidgetConfig = {
              version: CONFIG_VERSION,
              activeWidgetIds: tplActiveIds,
              hiddenWidgetIds: tplHiddenIds,
              layouts: {
                lg: patchMinSizes(tplLayouts.lg ?? []),
                md: patchMinSizes(tplLayouts.md ?? []),
                sm: patchMinSizes(tplLayouts.sm ?? []),
              },
            };
            configRef.current = tplConfig;
            setConfig(tplConfig);
            saveConfig(userId, tplConfig);
            widgetApi.saveConfig(tplConfig).catch(() => {});
            return;
          }
        } catch {
          // Template fetch failed — continue with hardcoded defaults
        }
        // No template either — push current localStorage config to API (migration)
        const local = configRef.current;
        if (local) {
          widgetApi.saveConfig(local).catch(() => {});
        }
      }
    }).catch(() => {
      // API unavailable — localStorage config is already loaded, continue silently
    });
  }, [userId, permittedIds]);

  const activeWidgets = useMemo(() => {
    const activeSet = new Set(config.activeWidgetIds);
    return config.activeWidgetIds
      .filter((id) => permittedIds.includes(id) && activeSet.has(id))
      .map((id) => WIDGET_MAP.get(id)!)
      .filter(Boolean) as WidgetDef[];
  }, [config.activeWidgetIds, permittedIds]);

  const availableWidgets = useMemo(() => {
    const activeSet = new Set(config.activeWidgetIds);
    return permittedWidgets.filter((w) => !activeSet.has(w.id));
  }, [config.activeWidgetIds, permittedWidgets]);

  const responsiveLayouts = useMemo<ResponsiveLayouts>(
    () => ({
      lg: config.layouts.lg,
      md: config.layouts.md,
      sm: config.layouts.sm,
    }),
    [config.layouts],
  );

  // Debounced layout persist — prevents feedback loops when resizing across breakpoints
  const layoutDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingLayoutRef = useRef<ResponsiveLayouts | null>(null);

  useEffect(() => () => {
    if (layoutDebounceRef.current) clearTimeout(layoutDebounceRef.current);
  }, []);

  const onLayoutChange = useCallback(
    (_layout: Layout, allLayouts: ResponsiveLayouts) => {
      const current = configRef.current;
      const nextLg = (allLayouts.lg ?? current.layouts.lg) as LayoutItem[];
      const nextMd = (allLayouts.md ?? current.layouts.md) as LayoutItem[];
      const nextSm = (allLayouts.sm ?? current.layouts.sm) as LayoutItem[];

      // Skip persist if layouts haven't actually changed — breaks the feedback loop
      if (
        layoutsEqual(nextLg, current.layouts.lg) &&
        layoutsEqual(nextMd, current.layouts.md) &&
        layoutsEqual(nextSm, current.layouts.sm)
      ) {
        return;
      }

      pendingLayoutRef.current = allLayouts;
      if (layoutDebounceRef.current) clearTimeout(layoutDebounceRef.current);
      layoutDebounceRef.current = setTimeout(() => {
        const layouts = pendingLayoutRef.current;
        if (!layouts) return;
        pendingLayoutRef.current = null;
        const cur = configRef.current;
        persist({
          ...cur,
          layouts: {
            lg: [...(layouts.lg ?? cur.layouts.lg)],
            md: [...(layouts.md ?? cur.layouts.md)],
            sm: [...(layouts.sm ?? cur.layouts.sm)],
          },
        });
      }, 200);
    },
    [persist],
  );

  const addWidget = useCallback(
    (id: string) => {
      const current = configRef.current;
      const def = WIDGET_MAP.get(id);
      if (!def || current.activeWidgetIds.includes(id)) return;

      const newItem: LayoutItem = {
        i: id,
        x: 0,
        y: Infinity,
        w: def.defaultSize.w,
        h: def.defaultSize.h,
        minW: def.minSize.w,
        minH: def.minSize.h,
      };

      persist({
        ...current,
        activeWidgetIds: [...current.activeWidgetIds, id],
        hiddenWidgetIds: current.hiddenWidgetIds.filter((i) => i !== id),
        layouts: {
          lg: [...current.layouts.lg, newItem],
          md: [...current.layouts.md, newItem],
          sm: [...current.layouts.sm, { ...newItem, w: 1, x: 0 }],
        },
      });
    },
    [persist],
  );

  const removeWidget = useCallback(
    (id: string) => {
      const current = configRef.current;
      persist({
        ...current,
        activeWidgetIds: current.activeWidgetIds.filter((i) => i !== id),
        hiddenWidgetIds: [...current.hiddenWidgetIds, id],
        layouts: {
          lg: current.layouts.lg.filter((l) => l.i !== id),
          md: current.layouts.md.filter((l) => l.i !== id),
          sm: current.layouts.sm.filter((l) => l.i !== id),
        },
      });
    },
    [persist],
  );

  const resetToDefaults = useCallback(() => {
    persist(buildDefaults(permittedIds));
  }, [permittedIds, persist]);

  return {
    activeWidgets,
    availableWidgets,
    layouts: responsiveLayouts,
    onLayoutChange,
    addWidget,
    removeWidget,
    resetToDefaults,
  };
}
